import { useCallback } from "react"
import { useMutation } from "urql"

import type { ProductTestFormPayload, ProductTestOverrideResultsPayload } from "../types"

import { useUpdateProductTestDocuments } from "./useUpdateProductTestDocuments"

import type {
  ProductTestMutationUpdateArgs,
  ProductTestUpdateFailure,
  ProductTestUpdateInput,
  ProductTestUpdateSuccess,
  UpdateProductTestMutation,
} from "@/graphql/codegen/graphql"
import { UpdateProductTestDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseUpdateProductTestReturn {
  updateProductTest: (
    payload: ProductTestFormPayload | ProductTestOverrideResultsPayload,
    productTestId: string,
    isLotUpdated?: boolean
  ) => Promise<boolean>
  isUpdating: boolean
}

export const useUpdateProductTest = (): UseUpdateProductTestReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { updateDocuments, isUpdatingDocuments } = useUpdateProductTestDocuments()
  const [{ fetching }, executeMutation] = useMutation<UpdateProductTestMutation, ProductTestMutationUpdateArgs>(
    UpdateProductTestDocument
  )

  const updateProductTest = useCallback(
    async (
      payload: ProductTestFormPayload | ProductTestOverrideResultsPayload,
      productTestId: string,
      isLotUpdated = false
    ): Promise<boolean> => {
      const { lotNumber, batchNumber, ...testValues } = payload.productTest
      const input = {
        ...testValues,
        results: payload.productTest.results?.map(({ associatedTestTemplateId, ...resultValues }) => ({
          ...resultValues,
          productTestResultId: resultValues.productTestResultId!,
        })),
        productTestId,
        isLotUpdated,
      } as ProductTestUpdateInput
      const { data, error } = await executeMutation({ input })
      const output = data?.productTest.update

      if (error || output?.__typename === "ProductTestUpdateFailure") {
        const message = error?.message ?? (output as ProductTestUpdateFailure).error.message
        notify({ type: NotificationType.Error, message })

        return false
      }

      const { productTestId: updatedProductTestId } = (output as unknown as ProductTestUpdateSuccess).productTest

      await updateDocuments({
        productTestId: updatedProductTestId,
        isLotUpdated,
        documentsToUpload: payload.documentsToUpload,
        documentsToDelete: payload.documentsToDelete,
        documentsToCopy: payload.documentsToCopy,
      })
      notify({ type: NotificationType.Success, message: "Product test data successfully updated." })

      return true
    },
    [executeMutation, updateDocuments, notify]
  )

  return { updateProductTest, isUpdating: fetching || isUpdatingDocuments }
}
